import type { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { setIsAuthenticated } from "@/redux/features/auth/auth.slice";
import {
  setCurrentFranchise,
  setCurrentRestaurant,
  setCurrentUser,
} from "@/redux/features/user/user.slice";
import {
  clearCart,
  setOrderType,
  setSelectedMenu,
  setSelectedTable,
} from "@/redux/slices/pos.slice";
import type { User } from "@/redux/services/user/user.type";
import type {
  Restaurant,
  RestaurantFranchise,
} from "@/redux/services/restaurant/restaurant.type";
import { logger } from "@/services/Logger";
import { resetApiCaches } from "@/redux/apiReset";

/**
 * Clears everything that belongs to the signed-in user in the persisted state
 * (auth, user, restaurant, franchise, POS cart and selections) and drops every
 * RTK Query cache. Shared by every logout path, including the API's 401
 * (SFRESTO-164).
 */
export const clearUserState = (dispatch: Dispatch<UnknownAction>): void => {
  dispatch(setIsAuthenticated(false));
  dispatch(setCurrentUser({} as User));
  dispatch(setCurrentRestaurant({} as Restaurant));
  dispatch(setCurrentFranchise({} as RestaurantFranchise));
  dispatch(clearCart());
  dispatch(setOrderType("dine_in"));
  dispatch(setSelectedTable(null));
  dispatch(setSelectedMenu(null));
  dispatch(resetApiCaches());
};

/**
 * Logs the user out: ends the API session (POST /api/logout), then
 * `clearUserState()`.
 *
 * Local state is cleared even when the server call fails (e.g. network down).
 */
export const performLogout = async (
  dispatch: Dispatch<UnknownAction>,
  serverLogout: () => Promise<unknown>
): Promise<void> => {
  try {
    await serverLogout();
  } catch (error) {
    logger.warn("Logout", "Server logout failed, clearing local state", error);
  }

  clearUserState(dispatch);
};
